import { useMemo } from 'react';

import { FilterRecord } from '@/services/types/pagination-types';

import { ADVANCED_FILTER_KEYS } from '../config/filterConfig';

/**
 * Calculate the number of active filters, excluding base filters.
 *
 * Special handling:
 * - Date range pairs (due_date_after + due_date_before) count as 1 filter
 * - Array filters count by their length (each selected value)
 * - Single-value filters count as 1
 *
 * @param filters - Current filter state
 * @param baseFilters - Base filters for the vertical (excluded from count)
 * @returns Number of active filters
 */
export function useActiveFilterCount(
  filters: FilterRecord,
  baseFilters: FilterRecord,
): number {
  return useMemo(() => {
    let count = 0;
    const countedPairs = new Set<string>();

    Object.entries(filters).forEach(([key, value]) => {
      // Skip base filters (immutable per vertical)
      if (baseFilters[key] === value) return;

      // Skip empty values
      if (!value) return;

      // Handle date range pairs as a single filter (use config keys, not magic strings)
      if (
        key === ADVANCED_FILTER_KEYS.DUE_DATE_AFTER ||
        key === ADVANCED_FILTER_KEYS.DUE_DATE_BEFORE
      ) {
        if (!countedPairs.has('due_date_range')) {
          countedPairs.add('due_date_range');
          count += 1;
        }
        return;
      }

      // Count array filters by their length
      if (Array.isArray(value)) {
        count += value.length;
      } else {
        count += 1;
      }
    });

    return count;
  }, [filters, baseFilters]);
}
